import React from "react";
import { MenuIcon, XIcon } from "../ui/icons";
import { LEFT_NAV_ITEMS, RIGHT_NAV_ITEMS } from "../../constants/navigation";
import { ReactComponent as Logo } from "../../assets/icons/logo.svg";

export const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(true);
  const [isElevated, setIsElevated] = React.useState(false);
  const lastScrollY = React.useRef(0);
  const rafId = React.useRef<number | null>(null);

  const toggleMenu = () => setIsMenuOpen((prev) => !prev);
  const closeMenu = () => setIsMenuOpen(false);

  const navLinkClass =
    "text-sm font-medium tracking-wide uppercase text-accentNavy transition-colors duration-200 hover:text-accentGold";

  React.useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current);
      }

      rafId.current = window.requestAnimationFrame(() => {
        const previousScrollY = lastScrollY.current;
        const scrollDelta = currentScrollY - previousScrollY;
        const hasScrolled = currentScrollY > 0;

        setIsElevated(hasScrolled);

        if (scrollDelta > 6 && hasScrolled && !isMenuOpen) {
          setIsVisible(false);
        } else if (scrollDelta < -6) {
          setIsVisible(true);
        }

        lastScrollY.current = currentScrollY;
        rafId.current = null;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current);
      }
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isMenuOpen]);

  React.useEffect(() => {
    if (isMenuOpen) {
      setIsVisible(true);
      // Prevent body scroll when menu is open
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  // Close menu when clicking outside or on escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isMenuOpen) {
        closeMenu();
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const menuButton = target.closest('button[aria-label="Toggle navigation"]');
      const menuPanel = target.closest('.mobile-menu-panel');
      const backdrop = target.closest('.mobile-menu-backdrop');
      
      // Don't close if clicking the toggle button (it handles its own toggle)
      if (menuButton) {
        return;
      }
      
      // Close if clicking backdrop or outside menu
      if (isMenuOpen && (backdrop || (!menuPanel && !menuButton))) {
        closeMenu();
      }
    };

    if (isMenuOpen) {
      document.addEventListener("keydown", handleEscape);
      // Use capture phase to catch clicks early
      document.addEventListener("click", handleClickOutside, true);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [isMenuOpen]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-transform duration-500 ease-out border-b border-accentGold/40 pointer-events-auto ${
        isVisible ? "translate-y-0" : "-translate-y-full"
      } ${isElevated ? "shadow-sm" : "shadow-none"}`}
    >
      <div className="absolute inset-0 bg-white/10 backdrop-blur-md" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-accentNavy/10" />

      <div className="relative mx-auto flex max-w-6xl items-center justify-between px-6 py-1 lg:px-10 overflow-visible">
        <nav className="hidden flex-1 items-center justify-start gap-8 md:flex">
          {LEFT_NAV_ITEMS.map((item) => (
            <a key={item.label} href={item.href} className={navLinkClass}>
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex flex-1 items-center justify-center md:flex-none">
            <a href="#home" className="transition-opacity duration-200 hover:opacity-80" onClick={closeMenu}>
              <Logo className="h-16 w-52" />
            </a>
        </div>

        <nav className="hidden flex-1 items-center justify-end gap-8 md:flex">
          {RIGHT_NAV_ITEMS.map((item) => (
            <a key={item.label} href={item.href} className={navLinkClass}>
              {item.label}
            </a>
          ))}
        </nav>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggleMenu();
          }}
          className="relative md:hidden z-[60] flex h-10 w-10 items-center justify-center rounded-lg text-accentNavy transition-colors hover:bg-accentNavy/10"
          aria-label="Toggle navigation"
          aria-expanded={isMenuOpen}
        >
          <span className="sr-only">Toggle navigation</span>
          {isMenuOpen ? (
            <XIcon className="h-6 w-6 text-accentNavy" />
          ) : (
            <MenuIcon className="h-6 w-6 text-accentNavy" />
          )}
        </button>
      </div>

      {/* Mobile Menu Backdrop */}
      {isMenuOpen && (
        <div 
          className="mobile-menu-backdrop fixed inset-0 bg-accentNavy/20 backdrop-blur-sm z-[45] md:hidden"
          onClick={closeMenu}
          aria-hidden="true"
        />
      )}

      {/* Mobile Menu Drawer */}
      <div
        className={`mobile-menu-panel fixed top-0 left-0 h-screen w-full bg-white/95 backdrop-blur-md shadow-2xl z-[50] transform transition-transform duration-300 ease-in-out md:hidden ${
          isMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full items-center justify-center text-center">
          {/* Header with close button */}
          <div className="flex items-center justify-center p-6 border-b border-accentNavy/10">
            <Logo className="h-12 w-40" />
            <button
              type="button"
              onClick={closeMenu}
              className="flex h-10 w-10 items-center justify-center rounded-lg text-accentNavy transition-colors hover:bg-accentNavy/10"
              aria-label="Close menu"
            >
              <XIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Navigation Items */}
          <nav className="flex-1 overflow-y-auto p-6">
            <div className="flex flex-col gap-1">
              {LEFT_NAV_ITEMS.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={closeMenu}
                  className="py-3 px-4 text-sm font-semibold uppercase tracking-wide text-accentNavy transition-colors rounded-lg hover:bg-accentGold/10 hover:text-accentGold"
                >
                  {item.label}
                </a>
              ))}
              <div className="my-2 h-px bg-accentNavy/10" />
              {RIGHT_NAV_ITEMS.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={closeMenu}
                  className="py-3 px-4 text-sm font-semibold uppercase tracking-wide text-accentNavy transition-colors rounded-lg hover:bg-accentGold/10 hover:text-accentGold"
                >
                  {item.label}
                </a>
              ))}
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Navbar;

